import React, { Component } from 'react';
import { List } from 'immutable';
import keycode from 'keycode';
import { createStyles } from 'app/utils/css';
import DropdownOverlay from 'app/components/layout/DropdownOverlay';
import Spinner from 'app/components/svgs/Spinner';
import Input, { INPUT_STYLE, InputVariant } from 'app/components/forms/Input';
import KeyboardNavigation, { IChildParams } from 'app/components/common/KeyboardNavigation';
import { OnFocusOutside } from 'app/utils/react';
import { ValueComponent } from 'app/components/forms/MultiSelect/Value';
import { OptionComponent } from 'app/components/forms/MultiSelect/Option';

// Can be used to easily construct the valuesComponent property for MultiSelect.
export { EnumValue, TextValue, Value, ValueComponent, ValueVariant } from 'app/components/forms/MultiSelect/Value';

// Can be used to easily construct the optionsComponent property for MultiSelect.
export {
  BooleanOption,
  EnumOption,
  Option,
  OptionComponent,
  TextOption,
} from 'app/components/forms/MultiSelect/Option';

// This will only work when the value can be converted to a string.
const defaultGetKey = (value: { [key: string]: unknown }) => value.toString();

interface IDeps {
  keyboardNavigation: IChildParams;
}

interface IProps<ValueType, OptionType> {
  closeOnAdd?: boolean;
  getOptionsKey: (value: OptionType, index: number) => string;
  getValuesKey: (value: ValueType, index: number) => string;
  query: string;
  isLoading?: boolean;
  noResults?: string;
  disabled?: boolean;
  onClose?: () => unknown;
  onQueryChange: (query: string) => unknown;
  onSelect: (option: OptionType) => unknown;
  onRemove: (value: ValueType) => unknown;
  options: List<OptionType>;
  optionsComponent: OptionComponent<OptionType>;
  placeholder?: string;
  removeOnBackspace?: boolean;
  values: List<ValueType>;
  valuesComponent: ValueComponent<ValueType>;
}

interface IState {
  isOpen: boolean;
}

/**
 * This component is mostly used as a helper for creating other multiselect menus. Some props
 * should be provided in a subsequent component that helps construct the MultiSelect component,
 * whereas other props are apart of the MultiSelect interface. Mostly, the `optionsComponent`
 * and `valuesComponent` should be provided by an intermediary component.
 *
 * @param {object} props
 * @param {List} props.options - A list of options to render in the menu.
 * @param {Component} props.optionsComponent - Component for rendering the options. It will
 * take a `index`, `itemRef`, option` and `onSelect` propType.
 * @param {List} props.values - A list of values to show that are selected.
 * @param {Component} props.valuesComponent - Component for rendering the value. It will take
 * an `onRemove` and `value` propType.
 */
class MultiSelectBase<ValueType, OptionType> extends Component<IProps<ValueType, OptionType> & IDeps, IState> {
  private static initialState = {
    isOpen: false,
  };

  public state = MultiSelectBase.initialState;

  private wrapperRef = React.createRef<HTMLDivElement>();
  private inputNode: HTMLInputElement | null = null;

  public render() {
    const {
      props: {
        getOptionsKey,
        getValuesKey,
        query,
        isLoading,
        keyboardNavigation: { itemRef },
        noResults,
        onRemove,
        disabled,
        options,
        optionsComponent: OptionsComponent,
        placeholder,
        values,
        valuesComponent: ValuesComponent,
      },
      state: { isOpen },
      inputRef,
      onSelect,
      onChange,
      onKeyDown,
      onOpen,
      wrapperRef,
      onClose,
    } = this;

    return (
      <Styles>
        {css => (
          <OnFocusOutside handler={onClose} getNode={this.getWrapper}>
            <div className={css('wrapper')} ref={wrapperRef}>
              <div className={css('input')}>
                {values.map((value, index) => (
                  <ValuesComponent
                    key={getValuesKey(value, index)}
                    onRemove={onRemove}
                    value={value}
                    disabled={disabled}
                  />
                ))}

                <Input
                  data-testid="multiselect"
                  getRef={inputRef}
                  onChange={onChange}
                  onKeyDown={onKeyDown}
                  disabled={disabled}
                  onFocus={onOpen}
                  placeholder={placeholder}
                  value={query}
                  variant={InputVariant.Borderless}
                />
              </div>

              {isOpen && (
                <DropdownOverlay isOpen={isOpen}>
                  <div className={css('scroll')}>
                    {isLoading && (
                      <div className={css('spinner')}>
                        <Spinner color="#999" size="1em" />
                      </div>
                    )}

                    {!isLoading &&
                      options.size > 0 &&
                      options.map((option, index) => (
                        <OptionsComponent
                          index={index}
                          itemRef={itemRef}
                          key={getOptionsKey(option, index)}
                          option={option}
                          onSelect={onSelect}
                        />
                      ))}

                    {!isLoading && options.size === 0 && noResults && (
                      <div className={css('noResults')}>{noResults}</div>
                    )}
                  </div>
                </DropdownOverlay>
              )}
            </div>
          </OnFocusOutside>
        )}
      </Styles>
    );
  }

  private inputRef = (node: HTMLInputElement | null) => {
    this.props.keyboardNavigation.inputRef(node);
    this.inputNode = node;
  };

  private onOpen = () => {
    this.setState({ isOpen: true });
  };

  private onClose = () => {
    const { onClose } = this.props;

    this.setState(MultiSelectBase.initialState);

    if (onClose) {
      onClose();
    }
  };

  private onSelect = (option: OptionType) => {
    const { closeOnAdd, onSelect } = this.props;

    onSelect(option);

    if (closeOnAdd) {
      this.onClose();
    }
  };

  private onChange = (query: string) => {
    this.props.onQueryChange(query);
  };

  // When the user hits escape, blur the input node and close the select menu.
  private onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const {
      props: { query, onRemove, removeOnBackspace, values },
      inputNode,
      onClose,
    } = this;
    const key = keycode(e.keyCode);

    if (key === 'esc') {
      if (inputNode) {
        inputNode.blur();
      }

      onClose();
    } else if (removeOnBackspace && key === 'backspace' && !query && values.size > 0) {
      onRemove(values.last()!);
    }
  };

  private getWrapper = () => this.wrapperRef.current;
}

const Styles = createStyles({
  wrapper: {
    display: 'flex',
    flexGrow: 1,
    position: 'relative',
  },
  input: {
    alignItems: 'center',
    display: 'flex',
    flexGrow: 1,
    flexWrap: 'wrap',
    ...INPUT_STYLE,
    padding: '0 5px',
  },
  noResults: {
    padding: '10px',
    fontSize: '1.2em',
    fontWeight: 600,
    textAlign: 'center',
  },
  scroll: {
    maxHeight: '20em',
    overflowY: 'auto',
  },
  spinner: {
    padding: '10px',
    fontSize: '1.6em',
    textAlign: 'center',
  },
});

export class MultiSelect<ValueType, OptionType> extends Component<IProps<ValueType, OptionType>> {
  public static defaultProps = {
    getOptionsKey: defaultGetKey,
    getValuesKey: defaultGetKey,
    query: '',
    options: List(),
    values: List(),
  };

  public render() {
    return (
      <KeyboardNavigation>
        {keyboardNavigation => <MultiSelectBase {...this.props} keyboardNavigation={keyboardNavigation} />}
      </KeyboardNavigation>
    );
  }
}
